import moment from "moment";

import { getInitialsFromFullName } from "../../../constants/constants";
import { SelectOptionType } from "../../../types/types";
import { formatMoney } from "../../billing/invoices/form/invoice-form.utils";
import { RecurringScheduleForm } from "../../work/pages/repeats/recurringSchedule";
import { SequenceForm } from "../../work/pages/sequences/emailSequence.types";
import { isInAppLink } from "./links";
import {
  formatProposalDate,
  normalizeRepeat,
  normalizeSequence,
  proposalDate,
  ProposalRepeat,
  ProposalRow,
  ProposalSequence,
  repeatText,
  repeatWindowForm,
  sequenceText,
  sequenceWindowForm,
} from "./proposal.utils";

/*
 * Ask ProCharted actions round 3 (owner 2026-10-26: "build it with the confirm
 * step for everything"): an email sequence or repeat schedule on an existing
 * work item, a new invoice, a payment, a time entry and a timer. Like new work
 * and new clients, the proposal only pre-fills the app's own window; nothing
 * happens until the person clicks Save / Start there.
 */

export type ActionProposalKind =
  | "start_sequence"
  | "repeat_work"
  | "create_invoice"
  | "record_payment"
  | "add_time"
  | "start_timer";

export const ACTION_PROPOSAL_KINDS: ActionProposalKind[] = [
  "start_sequence",
  "repeat_work",
  "create_invoice",
  "record_payment",
  "add_time",
  "start_timer",
];

/** The existing work item an action is about. */
export type ProposalWork = {
  workId: string;
  workName: string;
  customerId: string;
  customerName: string;
  customerType: "organization" | "individual";
  link: string | null;
};

type Base<K extends ActionProposalKind, F> = {
  kind: K;
  summary: string;
  warnings: string[];
  fields: F;
};

export type SequenceOnWorkProposal = Base<
  "start_sequence",
  ProposalWork & { sequence: ProposalSequence }
>;

export type RepeatOnWorkProposal = Base<
  "repeat_work",
  ProposalWork & {
    startDate: string | null;
    dueDate: string | null;
    alreadyRecurring: boolean;
    currentSchedule: string;
    repeat: ProposalRepeat;
  }
>;

export type ProposalInvoiceLine = {
  description: string;
  quantity: number;
  rate: number;
  amount: number;
  serviceId: string;
  serviceName: string;
};

export type InvoiceProposal = Base<
  "create_invoice",
  {
    customerId: string;
    customerName: string;
    customerType: "organization" | "individual";
    workId: string;
    workName: string;
    invoiceDate: string | null;
    dueDate: string | null;
    termId: string;
    termName: string;
    notes: string;
    includeUnbilledTime: boolean;
    unbilledEntries: number;
    unbilledHours: number;
    unbilledAmount: number;
    lines: ProposalInvoiceLine[];
  }
>;

export type ProposalPaymentLine = {
  invoiceId: string;
  invoiceNo: number;
  balance: number;
  amount: number;
};

export type PaymentProposal = Base<
  "record_payment",
  {
    customerId: string;
    customerName: string;
    customerType: "organization" | "individual";
    amount: number;
    paymentDate: string | null;
    paymentMethodId: string;
    paymentMethodName: string;
    notes: string;
    invoices: ProposalPaymentLine[];
  }
>;

export type TimeProposal = Base<
  "add_time",
  ProposalWork & {
    date: string | null;
    minutes: number;
    notes: string;
    isBillable: boolean;
  }
>;

export type TimerProposal = Base<"start_timer", ProposalWork>;

export type ActionProposal =
  | SequenceOnWorkProposal
  | RepeatOnWorkProposal
  | InvoiceProposal
  | PaymentProposal
  | TimeProposal
  | TimerProposal;

const text = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);

const num = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const money = (value: unknown) => Math.round(num(value) * 100) / 100;

const customerType = (value: unknown): "organization" | "individual" =>
  text(value) === "organization" ? "organization" : "individual";

const workOf = (f: Record<string, unknown>): ProposalWork => ({
  workId: text(f.workId),
  workName: text(f.workName),
  customerId: text(f.customerId),
  customerName: text(f.customerName),
  customerType: customerType(f.customerType),
  link: isInAppLink(text(f.link)) ? text(f.link) : null,
});

export const isActionKind = (kind: unknown): kind is ActionProposalKind =>
  ACTION_PROPOSAL_KINDS.includes(kind as ActionProposalKind);

/** Cleans one of the new kinds; null when something the window needs is missing. */
export const normalizeActionProposal = (
  kind: ActionProposalKind,
  f: Record<string, unknown>,
  summary: string,
  warnings: string[],
): ActionProposal | null => {
  const work = workOf(f);
  const base = { summary, warnings };
  switch (kind) {
    case "start_sequence": {
      const sequence = normalizeSequence(f.sequence);
      if (!work.workId || !sequence) return null;
      return {
        ...base,
        kind,
        summary: summary || `Email sequence on ${work.workName}`,
        fields: { ...work, sequence },
      };
    }
    case "repeat_work": {
      const repeat = normalizeRepeat(f.repeat);
      if (!work.workId || !repeat) return null;
      return {
        ...base,
        kind,
        summary: summary || `Repeat ${work.workName}`,
        fields: {
          ...work,
          startDate: proposalDate(f.startDate),
          dueDate: proposalDate(f.dueDate),
          alreadyRecurring: f.alreadyRecurring === true,
          currentSchedule: text(f.currentSchedule),
          repeat,
        },
      };
    }
    case "create_invoice": {
      const lines = (Array.isArray(f.lines) ? f.lines : [])
        .filter(isRecord)
        .slice(0, 20)
        .map((line) => {
          const quantity = num(line.quantity) > 0 ? num(line.quantity) : 1;
          const rate = Math.max(0, money(line.rate));
          return {
            description: text(line.description).slice(0, 500),
            quantity,
            rate,
            amount: money(quantity * rate),
            serviceId: text(line.serviceId),
            serviceName: text(line.serviceName),
          };
        });
      const includeUnbilledTime = f.includeUnbilledTime === true;
      const customerId = text(f.customerId);
      if (!customerId || (!includeUnbilledTime && !lines.length)) return null;
      return {
        ...base,
        kind,
        summary: summary || `Invoice for ${text(f.customerName)}`,
        fields: {
          customerId,
          customerName: text(f.customerName),
          customerType: customerType(f.customerType),
          workId: text(f.workId),
          workName: text(f.workName),
          invoiceDate: proposalDate(f.invoiceDate),
          dueDate: proposalDate(f.dueDate),
          termId: text(f.termId),
          termName: text(f.termName),
          notes: text(f.notes).slice(0, 1000),
          includeUnbilledTime,
          unbilledEntries: Math.max(0, Math.round(num(f.unbilledEntries))),
          unbilledHours: Math.max(0, money(f.unbilledHours)),
          unbilledAmount: Math.max(0, money(f.unbilledAmount)),
          lines,
        },
      };
    }
    case "record_payment": {
      const invoices = (Array.isArray(f.invoices) ? f.invoices : [])
        .filter(isRecord)
        .map((line) => ({
          invoiceId: text(line.invoiceId),
          invoiceNo: Math.round(num(line.invoiceNo)),
          balance: money(line.balance),
          amount: Math.max(0, money(line.amount)),
        }))
        .filter((line) => line.invoiceId && line.amount > 0);
      const customerId = text(f.customerId);
      const amount = money(f.amount);
      if (!customerId || !(amount > 0) || !invoices.length) return null;
      return {
        ...base,
        kind,
        summary: summary || `Payment from ${text(f.customerName)}`,
        fields: {
          customerId,
          customerName: text(f.customerName),
          customerType: customerType(f.customerType),
          amount,
          paymentDate: proposalDate(f.paymentDate),
          paymentMethodId: text(f.paymentMethodId),
          paymentMethodName: text(f.paymentMethodName),
          notes: text(f.notes).slice(0, 1000),
          invoices,
        },
      };
    }
    case "add_time": {
      const minutes = Math.round(num(f.minutes));
      if (!work.workId || !work.customerId || minutes < 1 || minutes > 1440)
        return null;
      return {
        ...base,
        kind,
        summary: summary || `Add time to ${work.workName}`,
        fields: {
          ...work,
          date: proposalDate(f.date),
          minutes,
          notes: text(f.notes).slice(0, 1000),
          isBillable: f.isBillable !== false,
        },
      };
    }
    case "start_timer":
      if (!work.workId || !work.customerId) return null;
      return {
        ...base,
        kind,
        summary: summary || `Start a timer on ${work.workName}`,
        fields: work,
      };
    default:
      return null;
  }
};

/** "1 h 30 min", "45 min", "2 h" */
export const durationText = (minutes: number): string => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return (
    [h ? `${h} h` : "", m ? `${m} min` : ""].filter(Boolean).join(" ") ||
    "0 min"
  );
};

const dollars = (value: number) => `$${formatMoney(value)}`;

const lineText = (line: ProposalInvoiceLine) =>
  `${line.description || line.serviceName || "Line"}: ${
    line.quantity === 1 ? "" : `${line.quantity} × `
  }${dollars(line.rate)}`;

/** Card rows for the new kinds. */
export const actionRows = (proposal: ActionProposal): ProposalRow[] => {
  const p = proposal;
  switch (p.kind) {
    case "start_sequence":
      return [
        { label: "Work item", value: p.fields.workName },
        { label: "Client", value: p.fields.customerName },
        { label: "Email sequence", value: sequenceText(p.fields.sequence) },
      ];
    case "repeat_work":
      return [
        { label: "Work item", value: p.fields.workName },
        { label: "Client", value: p.fields.customerName },
        {
          label: "Repeats now",
          value: p.fields.alreadyRecurring
            ? p.fields.currentSchedule || "Yes"
            : "",
        },
        {
          label: p.fields.alreadyRecurring ? "New schedule" : "Repeats",
          value: repeatText(p.fields.repeat),
        },
      ];
    case "create_invoice":
      return [
        { label: "Client", value: p.fields.customerName },
        { label: "Work item", value: p.fields.workName },
        {
          label: "Unbilled time",
          value:
            p.fields.includeUnbilledTime && p.fields.unbilledEntries
              ? `${p.fields.unbilledHours} h (${p.fields.unbilledEntries} ${
                  p.fields.unbilledEntries === 1 ? "entry" : "entries"
                }${
                  p.fields.unbilledAmount
                    ? `, ${dollars(p.fields.unbilledAmount)}`
                    : ""
                })`
              : "",
        },
        { label: "Lines", value: p.fields.lines.map(lineText).join("\n") },
        {
          label: "Invoice date",
          value: formatProposalDate(p.fields.invoiceDate),
        },
        {
          label: "Due",
          value: p.fields.termName || formatProposalDate(p.fields.dueDate),
        },
        { label: "Notes", value: p.fields.notes },
      ];
    case "record_payment":
      return [
        { label: "Client", value: p.fields.customerName },
        { label: "Amount", value: dollars(p.fields.amount) },
        {
          label: "Date",
          value: formatProposalDate(p.fields.paymentDate),
        },
        { label: "Method", value: p.fields.paymentMethodName },
        {
          label: "Applied to",
          value: p.fields.invoices
            .map(
              (line) => `Invoice #${line.invoiceNo}: ${dollars(line.amount)}`,
            )
            .join("\n"),
        },
        { label: "Notes", value: p.fields.notes },
      ];
    case "add_time":
      return [
        { label: "Work item", value: p.fields.workName },
        { label: "Client", value: p.fields.customerName },
        { label: "Date", value: formatProposalDate(p.fields.date) },
        { label: "Time", value: durationText(p.fields.minutes) },
        { label: "Billable", value: p.fields.isBillable ? "Yes" : "No" },
        { label: "Note", value: p.fields.notes },
      ];
    case "start_timer":
      return [
        { label: "Work item", value: p.fields.workName },
        { label: "Client", value: p.fields.customerName },
      ];
    default:
      return [];
  }
};

export const START_SEQUENCE_WARNING =
  "Clicking Start will start emailing the client.";

/** The app's own extra warnings for the new kinds. */
export const actionWarnings = (proposal: ActionProposal): string[] => {
  const extra: string[] = [];
  if (proposal.kind === "start_sequence") extra.push(START_SEQUENCE_WARNING);
  if (proposal.kind === "create_invoice") {
    const { invoiceDate, dueDate } = proposal.fields;
    if (invoiceDate && dueDate && dueDate < invoiceDate)
      extra.push("The due date is before the invoice date.");
  }
  if (proposal.kind === "record_payment") {
    const applied = money(
      proposal.fields.invoices.reduce((sum, line) => sum + line.amount, 0),
    );
    if (Math.abs(applied - proposal.fields.amount) >= 0.01)
      extra.push(
        `Only ${dollars(applied)} of the ${dollars(
          proposal.fields.amount,
        )} is applied to invoices: check the amounts in the window.`,
      );
  }
  return extra;
};

/** Card heading, the review button and the "nothing happened" line. */
export const actionCardText = (
  kind: ActionProposalKind,
): { heading: string; button: string; footer: string } => {
  switch (kind) {
    case "start_sequence":
      return {
        heading: "Ready to start",
        button: "Review & start",
        footer: "Nothing has been sent yet.",
      };
    case "start_timer":
      return {
        heading: "Ready to start",
        button: "Review & start",
        footer: "The timer hasn't started yet.",
      };
    default:
      return {
        heading: "Ready to save",
        button: "Review & save",
        footer: "Nothing has been saved yet.",
      };
  }
};

const clientOption = (
  id: string,
  name: string,
  type: "organization" | "individual",
): SelectOptionType => ({
  value: id,
  label: name || "Client",
  avatar: getInitialsFromFullName(name || "Client"),
  avatarColor: type === "organization" ? "purple" : "blue",
});

/** Router state read by the work Sequences tab (opens New Sequence pre-filled). */
export type AiSequenceState = { aiSequence: SequenceForm };
/** Router state read by the work Repeat tab (opens the schedule window pre-filled). */
export type AiRepeatState = { aiRepeat: ProposalRepeat };

export type AiInvoiceState = {
  defaultCustomer: SelectOptionType;
  aiInvoice: {
    invoiceDate: string | null;
    dueDate: string | null;
    termId: string;
    notes: string;
    includeUnbilledTime: boolean;
    lines: ProposalInvoiceLine[];
  };
};

export type AiPaymentState = {
  aiPayment: {
    customer: SelectOptionType;
    paymentDate: string | null;
    paymentMethodId: string;
    notes: string;
    amounts: Record<string, number>;
  };
};

/** Where Review opens: a page (with router state) or a window over the page. */
export type ActionTarget =
  | { type: "navigate"; to: string; state: unknown }
  | {
      type: "time";
      initial: AddTimeInitial;
    }
  | { type: "timer"; contact: SelectOptionType; work: SelectOptionType };

export type AddTimeInitial = {
  contact: SelectOptionType;
  work: SelectOptionType;
  /** Local day (midnight). */
  date: Date;
  hours: string;
  minutes: string;
  notes: string;
  isBillable: boolean;
};

const localDay = (value: string | null, now: moment.Moment): Date =>
  (value ? moment(value, "YYYY-MM-DD", true) : moment(now))
    .startOf("day")
    .toDate();

export const actionTarget = (
  proposal: ActionProposal,
  now = moment(),
): ActionTarget => {
  switch (proposal.kind) {
    case "start_sequence": {
      const state: AiSequenceState = {
        aiSequence: sequenceWindowForm(proposal.fields.sequence),
      };
      return {
        type: "navigate",
        to: `/work/${proposal.fields.workId}/sequences`,
        state,
      };
    }
    case "repeat_work": {
      const state: AiRepeatState = { aiRepeat: proposal.fields.repeat };
      return {
        type: "navigate",
        to: `/work/${proposal.fields.workId}/repeats`,
        state,
      };
    }
    case "create_invoice": {
      const f = proposal.fields;
      const params = new URLSearchParams({ customerId: f.customerId });
      if (f.workId && f.includeUnbilledTime) params.set("workId", f.workId);
      const state: AiInvoiceState = {
        defaultCustomer: clientOption(
          f.customerId,
          f.customerName,
          f.customerType,
        ),
        aiInvoice: {
          invoiceDate: f.invoiceDate,
          dueDate: f.dueDate,
          termId: f.termId,
          notes: f.notes,
          includeUnbilledTime: f.includeUnbilledTime,
          lines: f.lines,
        },
      };
      return {
        type: "navigate",
        to: `/billing/invoices/add?${params.toString()}`,
        state,
      };
    }
    case "record_payment": {
      const f = proposal.fields;
      const state: AiPaymentState = {
        aiPayment: {
          customer: clientOption(f.customerId, f.customerName, f.customerType),
          paymentDate: f.paymentDate,
          paymentMethodId: f.paymentMethodId,
          notes: f.notes,
          amounts: Object.fromEntries(
            f.invoices.map((line) => [line.invoiceId, line.amount]),
          ),
        },
      };
      return { type: "navigate", to: "/billing/payments/add", state };
    }
    case "add_time": {
      const f = proposal.fields;
      return {
        type: "time",
        initial: {
          contact: clientOption(f.customerId, f.customerName, f.customerType),
          work: { value: f.workId, label: f.workName || "Work item" },
          date: localDay(f.date, now),
          hours: String(Math.floor(f.minutes / 60) || ""),
          minutes: String(f.minutes % 60 || ""),
          notes: f.notes,
          isBillable: f.isBillable,
        },
      };
    }
    case "start_timer":
    default: {
      const f = proposal.fields;
      return {
        type: "timer",
        contact: clientOption(f.customerId, f.customerName, f.customerType),
        work: { value: f.workId, label: f.workName || "Work item" },
      };
    }
  }
};

/** The Repeat tab's window form for a proposed schedule on this work item. */
export const repeatFormForWork = (
  repeat: ProposalRepeat,
  anchor: { startDate: string | Date; dueDate: string | Date },
  now = moment(),
): RecurringScheduleForm =>
  repeatWindowForm(
    repeat,
    moment(anchor.startDate).format("YYYY-MM-DD"),
    moment(anchor.dueDate).format("YYYY-MM-DD"),
    now,
  );

/** Payment window rows with the proposed amounts filled in (others untouched). */
export const withProposedAmounts = <
  T extends { id: string; amountPaid: string; isChecked: boolean },
>(
  rows: T[],
  amounts: Record<string, number>,
): T[] =>
  rows.map((row) =>
    amounts[row.id] > 0
      ? {
          ...row,
          amountPaid: String(Math.round(amounts[row.id] * 100) / 100),
          isChecked: true,
        }
      : row,
  );
